import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BLOG_URL, DOCS_URL } from '@app/config';
import { JsonLdService, MetaService } from '@app/core';
import {
  BlogPost,
  BlogSectionComponent,
  CustomerLogoSectionComponent,
  TestimonialsSectionComponent,
  VideoSectionComponent,
} from '@app/widgets';
import { APP_STORE_PUBLISHING_FAQS } from '../../constants';
import { AppStorePublishingAutomationSectionComponent } from '../app-store-publishing-automation-section/app-store-publishing-automation-section.component';
import { AppStorePublishingCtaSectionComponent } from '../app-store-publishing-cta-section/app-store-publishing-cta-section.component';
import { AppStorePublishingDestinationsSectionComponent } from '../app-store-publishing-destinations-section/app-store-publishing-destinations-section.component';
import { AppStorePublishingFaqSectionComponent } from '../app-store-publishing-faq-section/app-store-publishing-faq-section.component';
import { AppStorePublishingFeaturesSectionComponent } from '../app-store-publishing-features-section/app-store-publishing-features-section.component';
import { AppStorePublishingHeroSectionComponent } from '../app-store-publishing-hero-section/app-store-publishing-hero-section.component';
import { AppStorePublishingHowItWorksSectionComponent } from '../app-store-publishing-how-it-works-section/app-store-publishing-how-it-works-section.component';
import { AppStorePublishingStatisticsSectionComponent } from '../app-store-publishing-statistics-section/app-store-publishing-statistics-section.component';

@Component({
  selector: 'app-app-store-publishing',
  imports: [
    AppStorePublishingAutomationSectionComponent,
    AppStorePublishingCtaSectionComponent,
    AppStorePublishingDestinationsSectionComponent,
    AppStorePublishingFaqSectionComponent,
    AppStorePublishingFeaturesSectionComponent,
    AppStorePublishingHeroSectionComponent,
    AppStorePublishingHowItWorksSectionComponent,
    AppStorePublishingStatisticsSectionComponent,
    BlogSectionComponent,
    CustomerLogoSectionComponent,
    TestimonialsSectionComponent,
    VideoSectionComponent,
  ],
  templateUrl: './app-store-publishing.component.html',
  styleUrl: './app-store-publishing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppStorePublishingComponent {
  protected readonly blogPosts: BlogPost[] = [
    {
      title: 'Announcing Capawesome Cloud Native Builds',
      description:
        'We are excited to introduce Capawesome Cloud Native Builds, a new feature that allows you to build your Capacitor apps directly in the cloud without the need for local development environments.',
      date: 'November 17, 2025',
      authorName: 'Robin Genz',
      authorDescription: 'Maintainer',
      authorImageUrl:
        'https://capawesome.io/assets/external/avatars.githubusercontent.com/u/13857929.jpg',
      link: `${BLOG_URL}announcing-capawesome-cloud-native-builds/`,
      tag: 'Product Update',
    },
    {
      title: 'Setup Apple App Store Publishing with Capawesome Cloud',
      description:
        'Learn how to configure Capawesome Cloud for seamless publishing to the Apple App Store, including setting up your certificates, provisioning profiles, and app metadata.',
      date: 'November 10, 2025',
      authorName: 'Robin Genz',
      authorDescription: 'Maintainer',
      authorImageUrl:
        'https://capawesome.io/assets/external/avatars.githubusercontent.com/u/13857929.jpg',
      link: `${DOCS_URL}app-store-publishing/destinations/apple-app-store/`,
      tag: 'Development',
    },
    {
      title: 'Setup Google Play Store Publishing with Capawesome Cloud',
      description:
        'This guide walks you through the steps to set up Capawesome Cloud for publishing your Android apps to the Google Play Store, including managing your keystore and app listings.',
      date: 'November 10, 2025',
      authorName: 'Robin Genz',
      authorDescription: 'Maintainer',
      authorImageUrl:
        'https://capawesome.io/assets/external/avatars.githubusercontent.com/u/13857929.jpg',
      link: `${DOCS_URL}app-store-publishing/destinations/google-play-store/`,
      tag: 'Development',
    },
  ];

  constructor(
    private readonly metaService: MetaService,
    private readonly jsonLdService: JsonLdService,
  ) {
    this.metaService.setDescription(
      'Automate your app store submissions to TestFlight, Apple App Store, and Google Play Store with Capawesome Cloud. One-click publishing with seamless integration.',
    );
    this.metaService.setTitle('App Store Publishing for Capacitor');
    this.metaService.setKeywords([
      'app store publishing',
      'Capacitor deploy',
      'iOS App Store',
      'Google Play Store',
    ]);
    this.jsonLdService.setStructuredData([
      JsonLdService.buildWebPageSchema(
        'App Store Publishing for Capacitor - Capawesome Cloud',
        'Automate your app store submissions to TestFlight, Apple App Store, and Google Play Store.',
        'https://cloud.capawesome.io/app-store-publishing/',
      ),
      JsonLdService.buildFaqSchema(APP_STORE_PUBLISHING_FAQS),
    ]);
  }
}
